/**
 * The home board around the fly scope: every panel of the reference clip, fed
 * with the chain's own numbers.
 *
 * The clip's six mapping stages become the six steps that put this brain on Arc
 * (connectome, prune, quantize, etch, seal, think) and play in step with the
 * camera's tour; its region traces are the cells each thought fired, grouped by
 * their BANC class; its contact map counts the synapses the 85 tapes carry between
 * those groups. Nothing here is decorative data: a panel with no answer yet says so.
 */

import type { Vector3 } from "three";

import { isThought, type Tick } from "./api";
import type { Connectome, NeuronGeometry } from "./geometry";
import { ANCHORS, STOPS, type ScopeFrame, type StopKey } from "./flyscope";
import { t, type TKey } from "./i18n";

const fmt = (n: number): string => n.toLocaleString("en-US");

// ---- the six groups the clip's region panels name ------------------------------
export const GROUP_KEYS = ["A", "B", "C", "D", "E", "F"] as const;

const GROUP_COLORS = ["#2bb3e6", "#7b5cf0", "#f2662e", "#5cc83f", "#e0457b", "#e8432f"];

/** -1 when a cell belongs to none of the six */
export function groupOf(cellClass: string, region: string, superClass: string): number {
  if (
    region === "optic_lobe" ||
    superClass.startsWith("visual") ||
    superClass === "optic_lobe_intrinsic"
  )
    return 0;

  if (cellClass.startsWith("antennal_lobe") || cellClass === "olfactory_receptor_neuron") return 1;

  if (cellClass === "kenyon_cell" || cellClass.startsWith("mushroom_body")) return 2;

  if (cellClass.startsWith("central_complex")) return 3;

  if (cellClass.startsWith("lateral_horn")) return 4;

  if (
    superClass === "descending" ||
    superClass === "ascending" ||
    superClass === "motor" ||
    region === "ventral_nerve_cord"
  )
    return 5;

  return -1;
}

export function cellGroups(geometry: NeuronGeometry): Int8Array {
  const { meta } = geometry;
  const groups = new Int8Array(geometry.regions.length);

  for (let i = 0; i < groups.length; i += 1) {
    groups[i] = groupOf(
      meta.cellClasses[geometry.cellClasses[i]] ?? "",
      meta.regions[geometry.regions[i]] ?? "",
      meta.superClasses[geometry.superClasses[i]] ?? "",
    );
  }

  return groups;
}

/** synapse records between the six groups, row = source group */
export function contactMatrix(groups: Int8Array, connectome: Connectome): number[][] {
  const matrix = GROUP_KEYS.map(() => GROUP_KEYS.map(() => 0));
  const { sources, targets } = connectome;

  for (let e = 0; e < sources.length; e += 1) {
    const a = groups[sources[e]];
    const b = groups[targets[e]];

    if (a >= 0 && b >= 0) matrix[a][b] += 1;
  }

  return matrix;
}

/** cells each thought fired, per group: rows = thoughts (oldest first) */
export function groupActivity(ticks: readonly Tick[], groups: Int8Array): number[][] {
  return ticks.flatMap((tick) => {
    if (!isThought(tick)) return [];

    const row = GROUP_KEYS.map(() => 0);

    for (const cell of tick.fired) {
      const g = groups[cell];

      if (g !== undefined && g >= 0) row[g] += 1;
    }

    return [row];
  });
}

/** each tour stop's own words: the where-box, its line, and the observation card's name */
const STOP_COPY: Readonly<Record<StopKey, { where: TKey; sub: TKey; name: TKey }>> = {
  specimen: { where: "bd.w.specimen", sub: "bd.ws.specimen", name: "bd.sn.specimen" },
  eyes: { where: "bd.w.eyes", sub: "bd.ws.eyes", name: "bd.sn.eyes" },
  cuticle: { where: "bd.w.cuticle", sub: "bd.ws.cuticle", name: "bd.sn.cuticle" },
  brain: { where: "bd.w.brain", sub: "bd.ws.brain", name: "bd.sn.brain" },
  optic: { where: "bd.w.optic", sub: "bd.ws.optic", name: "bd.sn.optic" },
  memory: { where: "bd.w.memory", sub: "bd.ws.memory", name: "bd.sn.memory" },
  map: { where: "bd.w.map", sub: "bd.ws.map", name: "bd.sn.map" },
};

/** what a running stage says it is doing, ST-01 .. ST-06 */
const RUNNING: readonly TKey[] = ["bd.r1", "bd.r2", "bd.r3", "bd.r4", "bd.r5", "bd.r6"];

// ---- small drawing helpers -------------------------------------------------------
const el = (id: string): HTMLElement | null => document.getElementById(id);

function setText(id: string, text: string): void {
  const node = el(id);

  if (node !== null && node.textContent !== text) node.textContent = text;
}

function bars(id: string, values: readonly number[], width: number, height: number, gap = 1): void {
  const svg = el(id);

  if (svg === null || values.length === 0) return;

  const max = Math.max(1, ...values);
  const w = (width - gap * (values.length - 1)) / values.length;

  svg.innerHTML = values
    .map((v, i) => {
      const h = Math.max(1, (v / max) * height);

      return `<rect x="${(i * (w + gap)).toFixed(2)}" y="${(height - h).toFixed(2)}" width="${w.toFixed(2)}" height="${h.toFixed(2)}"/>`;
    })
    .join("");
}

function histogram(values: ArrayLike<number>, bins: number, map: (v: number) => number): number[] {
  const out = Array.from({ length: bins }, () => 0);

  for (let i = 0; i < values.length; i += 1) {
    const b = Math.max(0, Math.min(bins - 1, map(values[i])));

    out[b] += 1;
  }

  return out;
}

// ---- the board -------------------------------------------------------------------
type Callout = {
  readonly anchor: Vector3;
  readonly title: string;
  readonly sub: string;
  readonly pill?: boolean;
};

export type BoardFacts = {
  readonly geometry: NeuronGeometry;
  readonly connectome: Connectome;
  readonly groups: Int8Array;
  readonly address: string;
};

export class Board {
  readonly #facts: BoardFacts;
  readonly #matrix: number[][];
  readonly #counts: {
    photoreceptors: number;
    olfactory: number;
    centralBrain: number;
    optic: number;
    kenyon: number;
    synapses: number;
  };
  #ticks: readonly Tick[] = [];
  #newest: Tick | null = null;
  #block = 0;
  #tick = 0;
  #tapes: number | null = null;
  #threshold = 0;
  #index = -1;
  #pair = -1;
  #idCell = -1;
  #lastWithin = -1;
  #frame: ScopeFrame | null = null;
  #project: ((p: Vector3) => [number, number, boolean]) | null = null;
  #idPosition: Vector3 | null = null;
  #turn = 0;
  #feed: { channel: number; at: Vector3; until: number } | null = null;

  constructor(facts: BoardFacts) {
    this.#facts = facts;
    const { geometry, connectome, groups } = facts;
    const { meta } = geometry;

    const classCount = (name: string): number => {
      const index = meta.cellClasses.indexOf(name);
      let n = 0;

      for (let i = 0; i < geometry.cellClasses.length; i += 1)
        if (geometry.cellClasses[i] === index) n += 1;

      return n;
    };

    this.#counts = {
      photoreceptors: classCount("photoreceptor_neuron"),
      olfactory: classCount("olfactory_receptor_neuron"),
      centralBrain: meta.regionCounts.get("central_brain") ?? 0,
      optic: meta.regionCounts.get("optic_lobe") ?? 0,
      kenyon: classCount("kenyon_cell"),
      synapses: connectome.sources.length,
    };
    this.#matrix = contactMatrix(groups, connectome);

    this.#paintStatic();
    this.#countUp();
  }

  // ---- inputs ----
  setHistory(ticks: readonly Tick[]): void {
    this.#ticks = ticks.filter(isThought);
    this.#newest = this.#ticks.at(-1) ?? null;
    this.#tick = this.#newest?.tick ?? this.#tick;
    this.#paintThoughts();
  }

  thought(tick: Tick): void {
    if (!isThought(tick)) return;

    this.#ticks = [...this.#ticks, tick].slice(-400);
    this.#newest = tick;
    this.#tick = tick.tick;
    this.#paintThoughts();
  }

  setBlock(block: number): void {
    if (block <= this.#block) return;

    this.#block = block;
    this.#paintStrip();
    setText("bdM6", fmt(block));
  }

  setBrain(tapes: number | null, threshold: number): void {
    this.#tapes = tapes;
    this.#threshold = threshold;
    this.#index = -1;
  }

  setTapeSizes(bytes: readonly number[]): void {
    bars("bdSpark4", bytes, 48, 16, 0.2);
  }

  setField(potentials: Int16Array | null, threshold: number): void {
    if (potentials === null || threshold <= 0) return;

    bars(
      "bdSpark5",
      histogram(potentials, 12, (v) => Math.floor((v / threshold) * 12)).map((n) => Math.log1p(n)),
      48,
      16,
    );
  }

  /** the scope's frame: the tour drives the stages, the strip and the labels */
  frame(frame: ScopeFrame, project: (p: Vector3) => [number, number, boolean], turn: number): void {
    this.#frame = frame;
    this.#project = project;
    this.#turn = turn;

    const within = Math.floor(((frame.progress * STOPS.length) % 1) * 20);

    if (frame.index !== this.#index || within !== this.#lastWithin) {
      this.#index = frame.index;
      this.#lastWithin = within;
      this.#paintStages(frame.index, within / 20);
    }

    this.#paintViewer(frame);
  }

  /** after a language switch: everything this class writes is re-said */
  relabel(): void {
    this.#index = -1;
    this.#pair = -1;
    this.#paintStatic();
    this.#paintThoughts();
    this.#paintStrip();

    if (this.#frame !== null && this.#project !== null)
      this.frame(this.#frame, this.#project, this.#turn);
  }

  // ---- painters ----
  #paintStatic(): void {
    const { geometry, connectome } = this.#facts;

    setText("bdSynapses", fmt(connectome.sources.length));
    setText("bdCellTypes", fmt(geometry.meta.cellTypes.length));
    setText(
      "bdM4",
      `${geometry.meta.superClasses.length} · ${fmt(geometry.meta.cellTypes.length)}`,
    );
    setText("bdConS", t("bd.conS", { n: fmt(connectome.sources.length) }));
    setText("bdBannerT", t("bd.bannerT", { n: fmt(geometry.meta.slots) }));

    // the stage sparklines, each read from the data its stage produced
    const superCounts = histogram(
      geometry.superClasses,
      geometry.meta.superClasses.length,
      (v) => v,
    );

    bars(
      "bdSpark1",
      superCounts.map((n) => Math.log1p(n)),
      48,
      16,
      0.6,
    );

    const degree = new Uint16Array(geometry.regions.length);

    for (let e = 0; e < connectome.sources.length; e += 1) degree[connectome.sources[e]] += 1;

    bars(
      "bdSpark2",
      histogram(degree, 10, (v) => v).map((n) => Math.log1p(n)),
      48,
      16,
    );
    bars(
      "bdSpark3",
      histogram(connectome.weights, 12, (v) => Math.floor(Math.log2(Math.abs(v) + 1))).map((n) =>
        Math.log1p(n),
      ),
      48,
      16,
    );

    this.#paintMatrix();
  }

  #countUp(): void {
    const total = this.#facts.geometry.meta.slots;
    const begin = performance.now();

    const step = (now: number): void => {
      const k = Math.min(1, (now - begin) / 1400);

      setText("bdNeurons", fmt(Math.round(total * (1 - (1 - k) ** 3))));

      if (k < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }

  #paintStrip(): void {
    const address = this.#facts.address;
    const short = address.length > 10 ? `${address.slice(0, 6)}…${address.slice(-4)}` : address;

    setText(
      "bdStrip",
      t("bd.strip", {
        addr: short.toUpperCase().replace("0X", "0x"),
        block: this.#block > 0 ? fmt(this.#block) : "—",
      }),
    );
  }

  #paintThoughts(): void {
    const newest = this.#newest;
    const tick = this.#tick > 0 ? fmt(this.#tick) : "—";

    setText("bdTickChip", `TICK ${tick}`);
    setText("bdActChip", t("bd.actChip", { n: tick }));
    setText("bdM1", newest === null ? "—" : `${newest.spiked} · ${newest.synapses}`);
    setText("bdActS", t("bd.actS", { n: fmt(this.#ticks.length) }));
    setText(
      "bdActFrom",
      t("bd.actFrom", { n: this.#ticks[0] === undefined ? "—" : fmt(this.#ticks[0].tick) }),
    );

    bars(
      "bdBars",
      this.#ticks.slice(-24).map((x) => x.spiked),
      56,
      22,
      1.2,
    );
    bars(
      "bdSpark6",
      this.#ticks.slice(-16).map((x) => x.spiked),
      48,
      16,
    );

    // the region traces: one spike per thought, as tall as the cells it fired there
    const activity = groupActivity(this.#ticks, this.#facts.groups);
    const latest = activity.at(-1);
    const totals = GROUP_KEYS.map((_, g) => activity.reduce((sum, row) => sum + row[g], 0));
    const peak = Math.max(1, ...activity.flat());
    const span = Math.max(1, activity.length);

    GROUP_KEYS.forEach((_, g) => {
      const svg = el(`bdTrace${g}`);

      if (svg !== null) {
        let d = "M0 20";

        activity.forEach((row, i) => {
          const x = ((i + 0.5) / span) * 292;
          const h = (row[g] / peak) * 18;

          if (h > 0)
            d += ` L${(x - 1.2).toFixed(1)} 20 L${x.toFixed(1)} ${(20 - h).toFixed(1)} L${(x + 1.2).toFixed(1)} 20`;
        });
        svg.innerHTML = `<path d="${d} L300 20"/>`;
      }

      const n = el(`bdActN${g}`);

      if (n !== null) {
        n.textContent = fmt(totals[g]);
        n.classList.toggle("is-hot", latest !== undefined && latest[g] > 0);
      }
    });

    bars("bdMini", totals, 36, 18, 1.4);
    const mini = el("bdMini");

    mini?.querySelectorAll("rect").forEach((rect, i) => rect.setAttribute("fill", GROUP_COLORS[i]));

    // the id tag follows the newest thought's first fired cell
    this.#idCell = newest?.fired[0] ?? -1;
    this.#idPosition = null;
  }

  #paintMatrix(): void {
    const host = el("bdMatrix");

    if (host === null) return;

    const matrix = this.#matrix;
    const max = Math.max(1, ...matrix.flat());
    const ramp = ["#f7efdc", "#d3eabd", "#b6e0e8", "#cfc1f2", "#f3a9c3", "#f4935f"];

    const cells = matrix
      .map((row, a) =>
        row
          .map((n, b) => {
            const level =
              n === 0
                ? 0
                : Math.min(
                    ramp.length - 1,
                    1 + Math.floor((Math.log1p(n) / Math.log1p(max)) * (ramp.length - 1)),
                  );

            return `<i data-a="${a}" data-b="${b}" style="background:${ramp[level]}" title="${GROUP_KEYS[a]}→${GROUP_KEYS[b]} · ${fmt(n)}"></i>`;
          })
          .join(""),
      )
      .join("");

    host.innerHTML = `${GROUP_KEYS.map((k) => `<b>${k}</b>`).join("")}${cells}`;
  }

  #paintPair(index: number): void {
    const pair = index <= 0 ? 0 : index - 1;

    if (pair === this.#pair) return;

    this.#pair = pair;
    const a = pair;
    const b = (pair + 1) % GROUP_KEYS.length;
    const label = `${GROUP_KEYS[a]}·${GROUP_KEYS[b]}`;
    const n = this.#matrix[a][b] + this.#matrix[b][a];

    setText("bdPair", t("bd.pair", { p: label }));
    setText("bdHot", label);
    setText("bdHotS", t("bd.hotS", { n: fmt(n) }));

    el("bdMatrix")
      ?.querySelectorAll<HTMLElement>("i")
      .forEach((cell) => {
        const ca = Number(cell.dataset.a);
        const cb = Number(cell.dataset.b);

        cell.classList.toggle("is-pair", (ca === a && cb === b) || (ca === b && cb === a));
      });

    const hex = el("bdHex");

    if (hex !== null) {
      const node = (i: number): [number, number] => {
        const angle = -Math.PI / 2 + (i * Math.PI) / 3;

        return [60 + Math.cos(angle) * 42, 56 + Math.sin(angle) * 42];
      };

      const ring = GROUP_KEYS.map((_, i) => node(i).join(",")).join(" ");
      const [ax, ay] = node(a);
      const [bx, by] = node(b);

      hex.innerHTML =
        `<polygon points="${ring}" class="ring"/>` +
        `<line x1="${ax}" y1="${ay}" x2="${bx}" y2="${by}" stroke="${GROUP_COLORS[a]}" class="hot"/>` +
        GROUP_KEYS.map((k, i) => {
          const [x, y] = node(i);
          const lx = 60 + (x - 60) * 1.32;
          const ly = 56 + (y - 56) * 1.32;

          return `<circle cx="${x}" cy="${y}" r="5" fill="${GROUP_COLORS[i]}"/><text x="${lx}" y="${ly + 3}">${k}</text>`;
        }).join("");
    }
  }

  #stageValue(stage: number): string {
    const { geometry, connectome } = this.#facts;

    switch (stage) {
      case 1:
        return t("bd.v1", { n: fmt(geometry.meta.slots) });
      case 2:
        return t("bd.v2", { n: fmt(connectome.sources.length) });
      case 3:
        return t("bd.v3", { n: "5" });
      case 4:
        return this.#tapes === null ? "—" : t("bd.v4", { n: fmt(this.#tapes) });
      case 5:
        return this.#threshold > 0 ? t("bd.v5", { n: fmt(this.#threshold) }) : "—";
      default:
        return this.#tick > 0 ? t("bd.v6", { n: fmt(this.#tick) }) : "—";
    }
  }

  #paintStages(active: number, within: number): void {
    setText("bdStageChip", t("bd.stageChip", { n: String(active).padStart(2, "0") }));

    for (let k = 1; k <= 6; k += 1) {
      const box = document.querySelector<HTMLElement>(`.bd-st[data-st="${k}"]`);
      const state = active === 0 || k > active ? "wait" : k === active ? "run" : "done";

      box?.setAttribute("data-state", state);
      setText(`bdSt${k}`, state === "wait" ? "—" : this.#stageValue(k));
      const status = el(`bdStS${k}`);

      if (status !== null) {
        const key: TKey =
          state === "wait"
            ? "bd.waiting"
            : state === "done"
              ? "bd.done"
              : (RUNNING[k - 1] ?? "bd.done");

        status.dataset.i18n = key;
        status.textContent = t(key);
      }

      const bar = el(`bdStBar${k}`);

      if (bar !== null)
        bar.style.width =
          state === "done" ? "100%" : state === "run" ? `${Math.round(within * 100)}%` : "0%";

      const step = document.querySelector<HTMLElement>(`.bd-step[data-step="${k}"]`);

      step?.setAttribute("data-state", state);
    }

    this.#paintPair(active);
  }

  #callouts(stop: StopKey): Callout[] {
    const c = this.#counts;

    switch (stop) {
      case "eyes":
        return [
          {
            anchor: ANCHORS.eye,
            title: t("bd.c.eye"),
            sub: t("bd.c.eyeS", { n: fmt(c.photoreceptors) }),
          },
          {
            anchor: ANCHORS.antenna,
            title: t("bd.c.antenna", { n: fmt(c.olfactory) }),
            sub: "",
            pill: true,
          },
        ];
      case "cuticle":
      case "brain":
        return [
          {
            anchor: ANCHORS.brain,
            title: t("bd.c.brain"),
            sub: t("bd.c.brainS", { n: fmt(c.centralBrain), s: fmt(c.synapses) }),
          },
        ];
      case "optic":
        return [
          {
            anchor: ANCHORS.optic,
            title: t("bd.c.optic"),
            sub: t("bd.c.opticS", { n: fmt(c.optic) }),
          },
        ];
      case "memory":
        return [
          {
            anchor: ANCHORS.memory,
            title: t("bd.c.memory"),
            sub: t("bd.c.memoryS", { n: fmt(c.kenyon) }),
          },
        ];
      default:
        return [];
    }
  }

  #paintViewer(frame: ScopeFrame): void {
    const project = this.#project;

    if (project === null) return;

    setText("bdZoom", `${frame.zoom.toFixed(2)}×`);
    setText("bdStop", t("bd.stop", { n: String(frame.index) }));

    // the scale bar: 48 px, said in millimetres at the brain's own scale
    setText("bdScale", `${(48 / frame.pxPerMm).toFixed(2)} mm`);

    const key = frame.stop;

    const copy = STOP_COPY[key];

    setText("bdWhere", t(copy.where));
    setText("bdWhereS", t(copy.sub));
    setText("bdWatching", t("bd.watching", { s: t(copy.name) }));
    setText(
      "bdTurn",
      t("bd.turn", {
        n: String(Math.round(this.#turn)),
        t: this.#tick > 0 ? fmt(this.#tick) : "—",
      }),
    );

    const cam = el("bdCam");
    const camKey: TKey = frame.moving ? "bd.zooming" : "bd.locked";

    if (cam !== null && cam.dataset.i18n !== camKey) {
      cam.dataset.i18n = camKey;
      cam.textContent = t(camKey);
    }

    setText("bdM2", t(camKey));
    setText("bdM3", `${Math.round(12 + (1 - frame.cuticle) * 88)}%`);
    setText("bdM5", `${Math.round(frame.parallax % 90)}°`);

    const marker = el("bdMarker");

    if (marker !== null) marker.style.left = `${(frame.progress * 100).toFixed(2)}%`;

    for (const tick of document.querySelectorAll<HTMLElement>(".bd-axis [data-go]")) {
      tick.classList.toggle("is-on", Number(tick.dataset.go) === frame.index);
    }

    const banner = el("bdBanner");

    if (banner !== null) banner.hidden = !(key === "map" && !frame.moving);

    // callouts: a ring on the anatomy, a leader, and a card
    const host = el("bdCallouts");

    if (host === null) return;

    const items = frame.moving ? [] : this.#callouts(key);
    const html: string[] = [];

    for (const item of items) {
      const [x, y, visible] = project(item.anchor);

      if (!visible) continue;

      if (item.pill === true) {
        html.push(
          `<span class="bd-pilltag" style="left:${x + 40}px;top:${y - 30}px">${item.title}</span>`,
        );
        continue;
      }

      const cx = x + 70;
      const cy = y - 120;

      html.push(
        `<i class="bd-ring" style="left:${x}px;top:${y}px"></i>` +
          `<svg class="bd-leader"><line x1="${x}" y1="${y}" x2="${cx}" y2="${cy + 52}"/></svg>` +
          `<div class="bd-card" style="left:${cx}px;top:${cy}px"><b>${item.title}</b><span>${item.sub}</span><i></i></div>`,
      );
    }

    const feed = this.#feed;

    if (feed !== null && performance.now() < feed.until) {
      const [x, y, visible] = project(feed.at);

      if (visible) {
        html.push(
          `<span class="bd-feedtag" style="left:${x + 26}px;top:${y - 34}px">${t("bd.feedTag", { n: String(feed.channel) })}</span>`,
        );
      }
    }

    // the newest thought's first fired cell, by its BANC root id, seen from inside
    if (
      this.#idCell >= 0 &&
      (key === "brain" || key === "optic" || key === "memory" || key === "cuticle")
    ) {
      const position = this.#idPosition;

      if (position !== null) {
        const [x, y, visible] = project(position);

        if (visible) {
          const id = this.#facts.geometry.ids[this.#idCell]?.toString() ?? "";

          html.push(
            `<span class="bd-idtag" style="left:${x - 190}px;top:${y + 70}px">id ${id}</span>`,
          );
        }
      }
    }

    const next = html.join("");

    if (host.dataset.html !== next) {
      host.dataset.html = next;
      host.innerHTML = next;
    }
  }

  /** a feed just landed: name its channel where the kernel charges it, for a few seconds */
  poke(channel: number, at: Vector3): void {
    this.#feed = { channel, at, until: performance.now() + 3_400 };
  }

  /** the scope tells the board where the tagged cell sits */
  setIdPosition(position: Vector3 | null): void {
    this.#idPosition = position;
  }

  get idCell(): number {
    return this.#idCell;
  }
}
